import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DisplayedSize, emptyOverride,
  MenuSection, NameTranslationType,
  OverrideInstruction,
  OverridePositionRequest, OverridePositionsMap,
  PositionTypeEnum,
  Size,
  SizeOverride
} from 'app/models/menu.model';
import { MenuService } from 'app/services/menu.service';
import { Organization } from 'app/models/organization.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MenuOverrideService } from 'app/services/menu-override.service';

@Component({
  selector: 'app-edit-size-modal',
  templateUrl: './edit-size-modal.component.html',
  styleUrls: ['./edit-size-modal.component.scss']
})
export class EditSizeModalComponent implements OnInit {
  initialNameTranslations: NameTranslationType;
  form: FormGroup;
  menuSectionId: string;
  changesInformationMessage = '';
  private selectedMenuSections = {};
  constructor(
    public dialogRef: MatDialogRef<EditSizeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditSizeModalComponentDataType,
    private menuService: MenuService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.menuSectionId = this.data.menuSectionId;
    this.initialNameTranslations = {...this.data.size.nameTranslations};

    this.form = new FormGroup({
      name: new FormControl(this.data.size.name, [Validators.required]),
    });

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        let nameValue = '';
        if (this.initialNameTranslations.hasOwnProperty(lang)) {
          nameValue = this.initialNameTranslations[lang];
        }
        else{
          this.initialNameTranslations[lang] = nameValue;
        }
        this.form.addControl(this.getFormLangName(lang), new FormControl(nameValue, []));
      }
    }

    this.form.valueChanges.subscribe(value => {
      this.formTouched = true;
    })
  }

  formTouched = false;

  private getFormLangName(lang: string) : string {
    return 'lang_' + lang;
  }

  private checkFormChanges() {
    let changes = [];
    if (this.data.size.name.trim() !== this.form.value['name'].trim()) {
      changes.push(`"${this.translate.instant('MENU.NAME')}"`);
    }
    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initialNameTranslations[lang].trim() !== this.form.value[this.getFormLangName(lang)].trim()) {
          changes.push(`"${this.translate.instant('MENU.NAME')} ${lang}"`);
        }
      }
    }
    return changes;
  }

  async submit() {
    if (!this.form.valid) {
      return;
    }

    let sizeOverride: SizeOverride;
    let isOverrideExists: boolean;

    let sizeOverridePosition = this.data.overridePositionsMap.getOverride(PositionTypeEnum.Size, this.data.size.externalId);

    // if override position does not exist create a new one
    if (!sizeOverridePosition){
      sizeOverridePosition = {
        positionType: PositionTypeEnum.Size,
        positionId: this.data.size.externalId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };
      sizeOverride = {};
    }
    else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      sizeOverride = JSON.parse(sizeOverridePosition.override) as SizeOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []

    if (this.data.size.name.trim() !== this.form.value['name'].trim()) {
      MenuOverrideService.overrideTextFiled(this.data.size, this.data.originalSize, 'name', this.form.value.name, isOverrideExists, overrideInstructions);
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initialNameTranslations[this.getFormLangName(lang)] !== this.form.value[this.getFormLangName(lang)]) {
          MenuOverrideService.overrideTranslationFiled(
            this.data.size,
            this.data.originalSize,
            'nameTranslations',
            lang,
            this.form.value[this.getFormLangName(lang)],
            isOverrideExists,
            sizeOverride,
            overrideInstructions);
        }
      }
    }

    if (overrideInstructions.length > 0) {
      const savedMenuSections = this.getSavedMenuSections();

      const productOverridePositionRequest: OverridePositionRequest = {
        ...sizeOverridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(this.menuSectionId,[productOverridePositionRequest], savedMenuSections);
    }
    this.dialogRef.close();
  }

  onMenuSelectedChanges($event: object) {
    this.selectedMenuSections = $event;
  }

  updateChangesInformationMessage() {
    let changes = this.checkFormChanges();

    this.changesInformationMessage = this.menuService.getUpdateInformationMessage(changes);
  }

  getSavedMenuSections(): string [] {
    return this.menuService.getSavedMenuSectionsIds(this.selectedMenuSections);
  }

  isSizeNameDifferent() {
    return this.data.originalSize.name.trim() !== this.form.value.name.trim();
  }

  isSizeNameLanguageDifferent(lang: string) {
    if (!this.data.originalSize.nameTranslations?.hasOwnProperty(lang)){
      return this.form.value[this.getFormLangName(lang)];
    }
    return this.data.originalSize.nameTranslations[lang].trim() !== this.form.value[this.getFormLangName(lang)].trim();
  }

  clearSizeName() {
    this.form.patchValue({
      name: this.data.originalSize.name,
    });
    this.updateChangesInformationMessage();
  }

  clearSizeTranslateName(lang: string) {
    const newValue = {};
    newValue[this.getFormLangName(lang)] =
      this.data.size.nameTranslations && this.data.originalSize.nameTranslations[lang]
        ? this.data.originalSize.nameTranslations[lang]
        : '';
    this.form.patchValue(newValue);
    this.updateChangesInformationMessage();
  }
}

export interface EditSizeModalComponentDataType {
  size: DisplayedSize;
  originalSize: Size;
  menuSectionId: string;
  menuSections: MenuSection [];
  organization: Organization;
  overridePositionsMap: OverridePositionsMap;
}
